import { useState } from 'react'
import moment from 'moment'

import { CellNumberCmp, ICellResult, CellStatus, IPlayer, CellSize } from '../types/common'
import { logos } from '../data/logos'
import { generateNumberFromDate } from '../utils/utils'
import playersData from '../data/players.json'

export function usePlayers() {
  const [players] = useState<IPlayer[]>(initPlayers())
  const [playerToFindIndex] = useState<number>(generateNumberFromDate(players.length, moment().format('YYYY-MM-DD')))
  // const [playerToFindIndex] = useState<number>(Math.floor(Math.random() * players.length))
  const [selectedPlayers, setSelectedPlayers] = useState<
    Array<{ firstName: string; lastName: string; cells: ICellResult[] }>
  >([])
  const [success, setSuccess] = useState<boolean>(getSuccess())

  function getSuccess(): boolean {
    const lastSuccessDate = localStorage.getItem('lastSuccessDate')

    return Boolean(lastSuccessDate && lastSuccessDate === moment().format('YYYY-MM-DD'))
  }

  function initPlayers(): IPlayer[] {
    return playersData.map((row) => ({
      lastName: row.lastName,
      firstName: row.firstName,
      country: row.country,
      positions: row.positions.split(', ').sort(),
      strongFoot: row.strongFoot,
      height: row.height,
      yearStart: row.yearStart,
      inActivity: row.inActivity,
      clubs: row.clubs.split(', ').sort(),
      goalsCount: row.goalsCount,
      championshipTitlesCount: row.championshipTitlesCount,
      cupTitlesCount: row.cupTitlesCount,
      selectionsCount: row.selectionsCount,
    }))
  }

  function selectPlayer(player: IPlayer) {
    const cells = comparePlayer(player)

    setSelectedPlayers([{ firstName: player.firstName, lastName: player.lastName, cells }, ...selectedPlayers])
  }

  function compareArrays(arr1: string[], arr2: string[]): CellStatus {
    let correctCount = 0

    for (const elem of arr1) {
      if (arr2.includes(elem)) {
        correctCount += 1
      }
    }

    if (arr1.length === arr2.length && correctCount === arr1.length) {
      return CellStatus.CORRECT
    }

    if (correctCount > 0) {
      return CellStatus.PARTIAL
    }

    return CellStatus.ERROR
  }

  function compareNumbers(nb1: number, nb2: number): CellNumberCmp {
    if (nb1 < nb2) {
      return CellNumberCmp.HIGHER
    }

    if (nb1 > nb2) {
      return CellNumberCmp.LOWER
    }

    return CellNumberCmp.EQUAL
  }

  function comparePlayer(player: IPlayer): ICellResult[] {
    const playerToFind = players[playerToFindIndex]

    console.log(playerToFind.lastName)

    const samePlayers = player.firstName === playerToFind.firstName && player.lastName === playerToFind.lastName

    if (samePlayers) {
      localStorage.setItem('lastSuccessDate', moment().format('YYYY-MM-DD'))
    }

    setSuccess(samePlayers)

    return [
      {
        size: CellSize.MEDIUM,
        value: player.country,
        status: player.country === playerToFind.country ? CellStatus.CORRECT : CellStatus.ERROR,
      },
      {
        size: CellSize.MEDIUM,
        value: player.positions.join(', '),
        status: compareArrays(player.positions, playerToFind.positions),
      },
      {
        size: CellSize.MEDIUM,
        value: player.strongFoot,
        status: player.strongFoot === playerToFind.strongFoot ? CellStatus.CORRECT : CellStatus.ERROR,
      },
      {
        size: CellSize.SMALL,
        value: `${player.height} cm`,
        status: player.height === playerToFind.height ? CellStatus.CORRECT : CellStatus.ERROR,
        numberCmp: compareNumbers(player.height, playerToFind.height),
      },
      {
        size: CellSize.SMALL,
        value: player.yearStart.toString(),
        status: player.yearStart === playerToFind.yearStart ? CellStatus.CORRECT : CellStatus.ERROR,
        numberCmp: compareNumbers(player.yearStart, playerToFind.yearStart),
      },
      {
        size: CellSize.SMALL,
        value: player.inActivity,
        status: player.inActivity === playerToFind.inActivity ? CellStatus.CORRECT : CellStatus.ERROR,
      },
      {
        size: CellSize.LARGE,
        // @ts-ignore
        logos: player.clubs.map((club) => logos[club]),
        status: compareArrays(player.clubs, playerToFind.clubs),
      },
      {
        size: CellSize.SMALL,
        value: player.goalsCount.toString(),
        status: player.goalsCount === playerToFind.goalsCount ? CellStatus.CORRECT : CellStatus.ERROR,
        numberCmp: compareNumbers(player.goalsCount, playerToFind.goalsCount),
      },
      {
        size: CellSize.SMALL,
        value: player.championshipTitlesCount.toString(),
        status:
          player.championshipTitlesCount === playerToFind.championshipTitlesCount
            ? CellStatus.CORRECT
            : CellStatus.ERROR,
        numberCmp: compareNumbers(player.championshipTitlesCount, playerToFind.championshipTitlesCount),
      },
      {
        size: CellSize.SMALL,
        value: player.cupTitlesCount.toString(),
        status: player.cupTitlesCount === playerToFind.cupTitlesCount ? CellStatus.CORRECT : CellStatus.ERROR,
        numberCmp: compareNumbers(player.cupTitlesCount, playerToFind.cupTitlesCount),
      },
      {
        size: CellSize.SMALL,
        value: player.selectionsCount.toString(),
        status: player.selectionsCount === playerToFind.selectionsCount ? CellStatus.CORRECT : CellStatus.ERROR,
        numberCmp: compareNumbers(player.selectionsCount, playerToFind.selectionsCount),
      },
    ]
  }

  return { players, playerToFindIndex, selectedPlayers, selectPlayer, success }
}
